import React, { useContext, useEffect, useRef, useState } from 'react'
import Autosuggest, {
    BlurEvent,
    ChangeEvent,
    RenderSuggestionParams,
    ShouldRenderReasons,
    SuggestionSelectedEventData,
    SuggestionsFetchRequestedParams,
} from 'react-autosuggest'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { AutoCompleteFetcher } from '@App/stores/search/models/select-option-hide'
import { StoresContext } from '@App/contexts'
import { observer } from 'mobx-react'
import './index.scss'

interface AutoCompleteInputProps {
    inputName: string
    onSelectedSuggestion(suggestion: string): void
    autoCompleteField: string
    autoCompleteFetcher: AutoCompleteFetcher
    onChange: (event: React.FormEvent<HTMLElement>, params: ChangeEvent) => void
    onBlur?: ((event: React.FocusEvent<HTMLElement>, params?: BlurEvent<string>) => void) | undefined
    ref?: React.Ref<HTMLInputElement> | undefined
    value?: string
    placeholder?: string
    onClick?: () => void
    onEnter?: () => void
}

const renderSuggestion = (suggestion: string, { query }: RenderSuggestionParams) => {
    const matches = match(suggestion, query)
    const parts = parse(suggestion, matches)
    return (
        <div className="suggestion-wrapper">
            {parts.map((part, index) =>
                part.highlight ? (
                    <span key={index}>
                        <strong>{part.text}</strong>
                    </span>
                ) : (
                    <span key={index}>{part.text}</span>
                )
            )}
        </div>
    )
}

const suggestionsFetchDebounceMs = 200

const AutoCompleteInput = ({
    inputName,
    onSelectedSuggestion,
    autoCompleteFetcher,
    autoCompleteField,
    onChange,
    onBlur,
    ref,
    value = '',
    placeholder = '',
    onClick,
    onEnter,
}: AutoCompleteInputProps) => {
    const { rightsholderStore, songwriterStore } = useContext(StoresContext)

    const [suggestions, setSuggestions] = useState<string[]>([])
    const suggestionsAbortControllerRef = useRef<AbortController | null>(null)
    const suggestionsFetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clearSuggestionsFetchTimeout = () => {
        if (suggestionsFetchTimeoutRef.current) {
            clearTimeout(suggestionsFetchTimeoutRef.current)
            suggestionsFetchTimeoutRef.current = null
        }
    }

    const abortSuggestionsRequest = () => {
        clearSuggestionsFetchTimeout()
        suggestionsAbortControllerRef.current?.abort()
        suggestionsAbortControllerRef.current = null
    }

    useEffect(() => abortSuggestionsRequest, [])

    const onSuggestionsFetchRequested = (request: SuggestionsFetchRequestedParams) => {
        abortSuggestionsRequest()

        suggestionsFetchTimeoutRef.current = setTimeout(async () => {
            const controller = new AbortController()
            suggestionsAbortControllerRef.current = controller

            try {
                const newSuggestions = await autoCompleteFetcher({
                    property: autoCompleteField,
                    searchTerm: request.value,
                    rightsholderId: rightsholderStore.optionalCurrentRightsholderId,
                    songwriterId: songwriterStore.optionalCurrentSongwriterId,
                    signal: controller.signal,
                })

                if (!controller.signal.aborted) {
                    setSuggestions(newSuggestions)
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    throw error
                }
            } finally {
                if (suggestionsAbortControllerRef.current === controller) {
                    suggestionsAbortControllerRef.current = null
                }
            }
        }, suggestionsFetchDebounceMs)
    }

    const onSuggestionSelected = (event: React.FormEvent<string>, data: SuggestionSelectedEventData<string>) => {
        onSelectedSuggestion(data.suggestion)
    }

    const getSuggestionValue = (suggestion: string) => suggestion

    const shouldRenderSuggestions = (newValue: string, reason: ShouldRenderReasons) => newValue.length > 2

    const onClearSuggestions = () => {
        abortSuggestionsRequest()
        setSuggestions([])
    }

    const keyPressHandler = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && onEnter) {
            abortSuggestionsRequest()
            onEnter()
        }
    }

    const inputProp = {
        placeholder: placeholder,
        value: value,
        onChange: onChange,
        name: inputName,
        onBlur: onBlur,
        ref: ref,
        'data-testid': inputName,
    }

    const containerProps = {
        onClick: (e: React.MouseEvent) => onClick && onClick(),
        onKeyPress: keyPressHandler,
    }

    return (
        <div className="auto-complete-input-holder">
            <Autosuggest<string>
                suggestions={suggestions}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                onSuggestionsClearRequested={onClearSuggestions}
                getSuggestionValue={getSuggestionValue}
                renderSuggestion={renderSuggestion}
                inputProps={inputProp}
                onSuggestionSelected={onSuggestionSelected}
                shouldRenderSuggestions={shouldRenderSuggestions}
                containerProps={containerProps}
                highlightFirstSuggestion={false}
            />
        </div>
    )
}

export default observer(AutoCompleteInput)
